import { Attendee } from './DtoAttendee';
import { PaymentContainerToken } from '../AllTypesExports';
import { ProductQuantity } from '../ecommerce/DtoProductQuantity';
import { Ticket, DtoTicket } from './DtoTicket';
import { DtoTicketPricingByDate } from './DtoTicketPricingByDate'

export interface DtoEventRegistration {
    eventId: number;
    attendees: Attendee[];
    selectedAddons: ProductQuantity[];
    promoCode: string | null;
    confirmationEmails: string[];
    summaryEmails: string[];
    paymentToken: PaymentContainerToken | null;
    taxRate: number;
    guestName: string | null;
    guestPhone: string | null;
    guestEmail: string | null;
    guestCompany: string | null;
    guestCompanyId: number | null;
    discount: number | 0;
    taxableDiscount: number | 0;
    recaptchaToken: string | null;
    taxExempt: boolean;
    isDonatingTransactionFee: boolean;
    purchaserId: number;
}

export class EventRegistration implements DtoEventRegistration {
    eventId!: number;
    attendees!: Attendee[];
    selectedAddons!: ProductQuantity[];
    promoCode!: string | null;
    confirmationEmails!: string[];
    summaryEmails!: string[];
    paymentToken!: PaymentContainerToken | null;
    taxRate!: number;
    guestName!: string | null;
    guestPhone!: string | null;
    guestEmail!: string | null;
    guestCompany!: string | null;
    guestCompanyId!: number | null;
    discount!: number | 0;
    taxableDiscount!: number | 0;
    recaptchaToken!: string | null;
    taxExempt!: boolean;
    isDonatingTransactionFee!: boolean;
    purchaserId!: number;
    currencyIcon!: string | "$";

    constructor(dtoRegistration: DtoEventRegistration) {
        Object.assign(this, dtoRegistration);
    }

    get purchasedTickets() {
        var tickets: Ticket[] = [];

        this.attendees.forEach(attendee => {
            tickets = tickets.concat(attendee.selectedTickets);
        });

        return tickets;
    }

    get uniquePurchasedTickets() {
        return this.purchasedTickets
                   .filter(function (value, index, self) { return self.findIndex(x => x.id == value.id) === index; });
    }

    get purchasedAddons() {
        var addons: ProductQuantity[] = [];

        if (this.selectedAddons && this.selectedAddons.length) {
            addons = addons.concat(this.selectedAddons);
        }

        this.attendees.forEach(attendee => {
            if (attendee.selectedAddons && attendee.selectedAddons.length) {
                addons = addons.concat(attendee.selectedAddons);
            }
        });

        return addons;
    }

    get purchasedAddonsForPaymentPlans() {
        var eligibleAddonsForPaymentPlan: { ID: number; Type: string; Quantity: number }[] = [];

        this.purchasedAddons.forEach(addon => {
            if (addon.paymentPlansEnabled) {
                eligibleAddonsForPaymentPlan.push({ ID: addon.id, Type: "Product", Quantity: addon.quantity });
            }
        });

        return eligibleAddonsForPaymentPlan;
    }

    get ticketAmounts() {
        var amounts: { [id: number]: number } = {};
        this.purchasedTickets.forEach(ticket => amounts[ticket.id] = (amounts[ticket.id] || 0) + 1);
        return amounts;
    }

    getTicketCount(ticket: DtoTicket) {
        return Math.ceil(this.getAttendeeCount(ticket.id) / ticket.seatsPerTicket);
    }

    getAttendeeCount(ticketID: number) {
        return this.ticketAmounts[ticketID] || 0;
    }

    get ticketTotal() {
        return this.selectedTicketsTotals.subTotal;
    }

    get ticketTotalForPaymentPlan() {
        return this.selectedTicketsTotals.totalForPaymentPlan;
    }

    get ticketTaxableSubTotalForPaymentPlan() {
        return this.selectedTicketsTotals.taxableSubTotalForPaymentPlan;
    }

    get taxableTicketTotal() {
        return this.taxExempt ? 0 : this.selectedTicketsTotals.taxableSubTotal;
    }

    get addonTotal() {
        var total = 0;
        this.purchasedAddons.forEach(addon => total += addon.subTotal);
        return total;
    }

    get addonTotalForPaymentPlan() {
        var total = 0;
        this.purchasedAddons.forEach(addon => total += addon.totalForPaymentPlan);
        return total;
    }

    get addonTaxableSubTotalForPaymentPlan() {
        var taxableTotal = 0;

        this.purchasedAddons.forEach(addon => taxableTotal += addon.taxableSubTotalForPaymentPlan);
        return taxableTotal;
    }

    get eligibleItemsForPaymentPlan() {
        var eligibleItemsForPaymentPlan: { ID: number; Type: string; Quantity: number }[] = [];
        if (this.selectedTicketsForPaymentPlans) {
            eligibleItemsForPaymentPlan.push(...this.selectedTicketsForPaymentPlans);
        }

        if (this.purchasedAddonsForPaymentPlans) {
            eligibleItemsForPaymentPlan.push(...this.purchasedAddonsForPaymentPlans);
        }

        return eligibleItemsForPaymentPlan;
    }

    get taxableAddonTotal() {
        if (this.taxExempt) {
            return 0;
        }

        var total = 0;
        this.purchasedAddons.forEach(addon => total += addon.taxableSubTotal);
        return total;
    }

    get taxableSubTotal() {
        const val = this.taxExempt ? 0 : this.taxableTicketTotal + this.taxableAddonTotal - this.taxableDiscount;
        return Math.max(0, val);
    }

    get subTotal() {
        const val = this.ticketTotal + this.addonTotal - this.discount;
        return Math.max(0, val);
    }

    get taxableSubTotalForPaymentPlan() {
        const val = this.taxExempt ? 0 : this.ticketTaxableSubTotalForPaymentPlan + this.addonTaxableSubTotalForPaymentPlan - this.taxableDiscount;
        return Math.max(0, val);
    }

    get taxTotalForPaymentPlan() {
        return Math.round(this.taxableSubTotalForPaymentPlan * (this.taxRate * 100)) / 100;
    }

    get eligibleForPaymentPlanAmount() {
        const val = this.ticketTotalForPaymentPlan + this.addonTotalForPaymentPlan + this.taxTotalForPaymentPlan - this.discount;
        return Math.max(0, val);
    }

    get subTotalFormatted() {
        return this.currencyIcon + this.subTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    get taxTotal() {
        return Math.round(this.taxableSubTotal * (this.taxRate * 100)) / 100;
    }

    get taxTotalFormatted() {
        return this.currencyIcon + this.taxTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    get selectedTicketsTotals() {
        return this.getTicketsSubtotals(this.uniquePurchasedTickets);
    }

    getTicketPriceIncreaseMessage(ticketPricingByDate: DtoTicketPricingByDate) {

        if (!ticketPricingByDate) return '';

        const { priceToday, nextPriceIncreaseDate, nextPriceIncreaseValue } = ticketPricingByDate;

        if (!nextPriceIncreaseDate || nextPriceIncreaseValue == null) return '';

        if (priceToday >= nextPriceIncreaseValue) return '';

        const priceAsString = this.currencyIcon + nextPriceIncreaseValue.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        const date = new Date(nextPriceIncreaseDate);
        const formattedDate = date.toLocaleDateString('en-US', {
            year: '2-digit',
            month: 'numeric',
            day: 'numeric'
        });

        return `Price will increase to ${priceAsString} on ${formattedDate}`;        
    }

    get selectedTicketsForPaymentPlans() {
        return this.getTicketsForPaymentPlans(this.uniquePurchasedTickets);
    }

    getTicketsSubtotals(tickets: Ticket[]) {
        var subTotal = 0;
        var taxableSubTotal = 0;
        var totalForPaymentPlan = 0;
        var taxableSubTotalForPaymentPlan = 0;

        tickets.forEach(ticket => {
            var quantity = this.getTicketCount(ticket);
            var ticketPrice = ticket.calculateSubtotal(quantity);
            subTotal += ticketPrice;
            taxableSubTotal += ticket.taxable ? ticketPrice : 0;
            totalForPaymentPlan += ticket.paymentPlansEnabled ? ticketPrice : 0;
            taxableSubTotalForPaymentPlan += ticket.paymentPlansEnabled && ticket.taxable ? ticketPrice : 0;
        });

        return { subTotal, taxableSubTotal, totalForPaymentPlan, taxableSubTotalForPaymentPlan };
    }

    getTicketsForPaymentPlans(tickets: Ticket[]) {
        var eligibleTicketsForPaymentPlan: { ID: number; Type: string; Quantity: number }[] = [];

        tickets.forEach(ticket => {

            if (ticket.paymentPlansEnabled) {
                var quantity = this.getTicketCount(ticket);

                eligibleTicketsForPaymentPlan.push({ ID: ticket.id, Type: "Ticket", Quantity: quantity });
            }
        });

        return eligibleTicketsForPaymentPlan;
    }

    getAttendeeSubtotal(attendee: Attendee) {
        let total = 0;

        // Track which tickets have been processed to avoid duplication
        const processedTickets = new Set();

        attendee.selectedTickets.forEach((ticket, index) => {
            if (!processedTickets.has(ticket.id)) {
                // Mark this ticket type as processed
                processedTickets.add(ticket.id);

                // Get all attendees for this specific ticket type
                const attendeesForTicket = this.attendees.filter(a => a.selectedTickets.some(t => t.id === ticket.id));

                // Calculate how many seats per ticket and the number of such tickets this attendee has
                const seatsPerTicket = ticket.seatsPerTicket;
                const totalTicketPrice = this.getTicketPrice(ticket, attendee, true);
            
                // Calculate the base price per seat
                const basePricePerSeat = Math.floor((totalTicketPrice / seatsPerTicket) * 100) / 100;
                let priceAccumulated = 0;

                let attendeeTicketCounter = 1;  // Track attendees based on ticket instance relative to seatsPerTicket
                attendeesForTicket.forEach((at, i) => {
                    let priceForThisAttendee = basePricePerSeat;

                    // Check if this is the last attendee for this ticket
                    if (attendeeTicketCounter === ticket.seatsPerTicket) {
                        // Adjust the price for the last attendee to include any remainder
                        priceForThisAttendee = totalTicketPrice - priceAccumulated;

                        attendeeTicketCounter = 0; // Reset for next ticket instance of same type (back to 1 by end of loop)
                        priceAccumulated = 0;
                    } else {
                        priceAccumulated += priceForThisAttendee;
                    }

                    // Only add to the total if the current attendee being processed matches the original attendee being calculated
                    if (at.uniqueId === attendee.uniqueId) {
                        total += priceForThisAttendee;
                    }

                    attendeeTicketCounter++;
                });
            }
        });

        // Add the price of any selected add-ons
        attendee.selectedAddons.forEach(addon => total += addon.subTotal);

        return total;
    }

    getAttendeeSubtotalFormated(attendee: Attendee) {
        var total = this.getAttendeeSubtotal(attendee);

        return this.currencyIcon + total.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }

    getTicketPrice(ticket: Ticket, attendee?: Attendee, attendeeSubtotal: boolean = false) {
        // First look out for multi seat tickets - We only want to charge one attendee per table rather than everyone
        // If it is method used for calculation of attendee subtotal this is not required because we have detailed logic there
        if (attendee && ticket.seatsPerTicket > 1 && !attendeeSubtotal) {
            var attendeeIndex = this.attendees
                .filter(a => a.hasSelectedTicket(ticket.id)) // of the attendees that selected this ticket
                .findIndex(a => a.matches(attendee));        // where does this attendee fall?

            var seatNumber = attendeeIndex % ticket.seatsPerTicket; // which seat does this attendee occupy at the table?

            // We only charge the head of the table
            if (seatNumber > 0) {
                return 0;
            }
        }

        // Standard case
        var quantity = this.getTicketCount(ticket);
        return ticket.calculatePrice(quantity);
    }

    getTicketPriceString(ticket: Ticket, attendee?: Attendee, attendeeSubtotal: boolean = false) {
        var price = this.getTicketPrice(ticket, attendee, attendeeSubtotal);

        return this.currencyIcon + price.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
}

export function createEventRegistration(dtoRegistration?: DtoEventRegistration | any) {
    let obj = <DtoEventRegistration>{
        eventId: 0,
        attendees: [],
        selectedAddons: [],
        promoCode: null,
        confirmationEmails: [],
        summaryEmails: [],
        paymentToken: null,
        taxRate: 0,
        guestName: '',
        guestPhone: null,
        guestEmail: null,
        guestCompany: null,
        guestCompanyId: null,
        discount: 0,
        taxableDiscount: 0,
        recaptchaToken: '',
        taxExempt: false,
        isDonatingTransactionFee: false,
        purchaserId: 0
    };

    if (dtoRegistration) {
        Object.assign(obj, dtoRegistration);
    }

    return new EventRegistration(obj);
}
